// ============================================================
// Symbol Mapping — Unified symbols (sql/060-061)
//
// A unifier (XAUUSD.U) groups synthetic symbols of one instrument
// (XAUUSD.S / .I / .V) for reporting: its longs, shorts and net are the sum
// of its members. Defined in Taiga only; never mapped to an LP, never hedged.
//
// Routes (BFF, same paths as C++):
//   GET    /api/v1/mappings/unified[?platform=]
//   GET    /api/v1/mappings/unified/candidates?platform=MT5
//   POST   /api/v1/mappings/unified          {name, platform, description?, members[]}
//   PUT    /api/v1/mappings/unified/{id}     {name?, description?, members?} (members replaced whole)
//   DELETE /api/v1/mappings/unified/{id}     members become stand-alone symbols
//
// The backend owns every rule (name taken, too few members, spec mismatch,
// member in another unifier, concurrent change). Its `error` text is shown;
// `code` drives behaviour (409 reloads).
// ============================================================

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const BASE = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

type UPlatform = 'MT5' | 'CTRADER';
const PLATFORMS: { value: UPlatform; label: string }[] = [
  { value: 'MT5',     label: 'MT5' },
  { value: 'CTRADER', label: 'cTrader' },
];
const platformName = (p: string) => (p === 'CTRADER' ? 'cTrader' : p);

interface Unifier {
  id:              number;
  name:            string;
  platform:        string;
  description:     string;
  members:         string[];
  contract_size:   number | null;
  currency_base:   string | null;
  currency_profit: string | null;
  /** false: the platform's MASTER could not be read, so members were not checked. */
  checked:         boolean;
  valid:           boolean;
  invalid_reasons: { code?: string; message: string }[];
  created_at?:     string;
  updated_at?:     string;
  created_by?:     string;
  updated_by?:     string;
}

/** A set of symbols that can go together: same contract size and currencies. */
interface CandidateGroup {
  contract_size:   number;
  currency_base:   string;
  currency_profit: string;
  symbols:         string[];
}

/** A backend refusal, as sent: { error, code, details }. */
class UnifiedError extends Error {
  constructor(message: string, public status: number, public code?: string, public details?: any) { super(message); }
}

async function uapi<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: { ...(opts.body ? { 'Content-Type': 'application/json' } : {}), ...opts.headers },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new UnifiedError(body?.error || `HTTP ${res.status}`, res.status, body?.code, body?.details);
  }
  return body as T;
}

// The common root of the chosen symbols plus '.U' ('XAUUSD.S', 'XAUUSD.I' →
// 'XAUUSD.U'). Empty when they share no meaningful root.
function suggestName(symbols: string[]): string {
  if (symbols.length < 2) return '';
  let root = symbols[0];
  for (const s of symbols.slice(1)) {
    let i = 0;
    while (i < root.length && i < s.length && root[i] === s[i]) i++;
    root = root.slice(0, i);
  }
  root = root.replace(/[._\-\s]+$/, '');
  return root.length >= 3 ? `${root}.U` : '';
}

const specLine = (cs: number | null | undefined, base?: string | null, profit?: string | null) =>
  `${cs != null ? cs.toLocaleString() : '—'} · ${base ?? '—'}/${profit ?? '—'}`;

// Details of MEMBER_SPEC_MISMATCH: each member's spec, whatever its shape.
function mismatchLines(details: any): string[] {
  const list = details?.members;
  if (!Array.isArray(list)) return [];
  return list.map((m: any) => {
    if (typeof m === 'string') return m;
    const sym = m?.symbol ?? m?.name ?? '';
    return `${sym}  ${specLine(m?.contract_size, m?.currency_base, m?.currency_profit)}`;
  });
}

const iCls = 'bg-[#232225] border border-[#606060] rounded px-2 py-1 text-xs text-white placeholder-[#888] focus:outline-none focus:border-[#49b3b3]';
const btn = (tone: 'ok' | 'plain' | 'danger', disabled = false): React.CSSProperties => disabled
  ? { backgroundColor: '#2a2a2c', color: '#777', border: '1px solid #383838', cursor: 'not-allowed' }
  : tone === 'ok'     ? { backgroundColor: '#162a1c', color: '#6aaa78', border: '1px solid #2f6a3d' }
  : tone === 'danger' ? { backgroundColor: '#2c1417', color: '#d07070', border: '1px solid #5a2530' }
  :                     { backgroundColor: '#2a2a2c', color: '#fff',    border: '1px solid #404040' };

// ── Status badge ─────────────────────────────────────────────
function StatusBadge({ u }: { u: Unifier }) {
  const base = { fontSize: 11, fontWeight: 600, padding: '0 6px', lineHeight: '16px', borderRadius: 3 } as const;
  if (!u.checked) {
    return <span title="The platform's MASTER could not be read, so the members were not checked."
      style={{ ...base, color: '#c09060', border: '1px solid #8a6d44' }}>Members not checked</span>;
  }
  if (!u.valid) {
    const tip = (u.invalid_reasons ?? []).map((r) => r.message).join('\n')
      + '\nIt still adds up its members. Edit its members or delete it.';
    return <span title={tip} style={{ ...base, color: '#d07070', border: '1px solid #8a4a52' }}>Invalid</span>;
  }
  return <span style={{ ...base, color: '#6aaa78', border: '1px solid #5a8a68' }}>Valid</span>;
}

// ── Create / edit dialog ─────────────────────────────────────
function UnifierDialog({ editing, onClose, onSaved }: {
  editing: Unifier | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [platform, setPlatform] = useState<UPlatform>((editing?.platform as UPlatform) ?? 'MT5');
  const [name, setName]         = useState(editing?.name ?? '');
  const [nameTouched, setNameTouched] = useState(!!editing);
  const [description, setDescription] = useState(editing?.description ?? '');
  const [members, setMembers]   = useState<string[]>(editing?.members ?? []);
  const [groupKey, setGroupKey] = useState<string | null>(null);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState<{ text: string; lines: string[] } | null>(null);

  const { data: candData, isFetching: loadingCand, error: candErr } = useQuery({
    queryKey: ['unified-candidates', platform],
    queryFn:  () => uapi<{ groups: CandidateGroup[]; platform: string }>(`/api/v1/mappings/unified/candidates?platform=${platform}`),
    staleTime: 10_000,
  });

  const keyOf = (g: { contract_size: number | null; currency_base: string | null; currency_profit: string | null }) =>
    `${g.contract_size}|${g.currency_base}|${g.currency_profit}`;

  // Candidates leave out symbols already in a unifier, so an edit adds its own
  // members back to the group of its spec (or as a group of its own).
  const groups = useMemo<CandidateGroup[]>(() => {
    const list = (candData?.groups ?? []).map((g) => ({ ...g, symbols: [...g.symbols] }));
    if (editing) {
      const k = keyOf(editing);
      let g = list.find((x) => keyOf(x) === k);
      if (!g) {
        g = { contract_size: editing.contract_size ?? 0, currency_base: editing.currency_base ?? '', currency_profit: editing.currency_profit ?? '', symbols: [] };
        list.unshift(g);
      }
      for (const m of editing.members) if (!g.symbols.includes(m)) g.symbols.push(m);
      g.symbols.sort();
    }
    return list;
  }, [candData, editing]);

  const activeKey = editing ? keyOf(editing) : groupKey;

  const toggle = (g: CandidateGroup, sym: string) => {
    const k = keyOf(g);
    // Members come from one group only: picking in another group starts over.
    const next = k !== activeKey ? [sym] : members.includes(sym) ? members.filter((m) => m !== sym) : [...members, sym];
    if (!editing) setGroupKey(next.length ? k : null);
    setMembers(next);
    if (!nameTouched) setName(suggestName(next));
  };

  const canSave = !busy && name.trim().length > 0 && members.length >= 2;

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      if (editing) {
        await uapi(`/api/v1/mappings/unified/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: name.trim(), description: description.trim(), members }),
        });
        onSaved(`${name.trim()} saved`);
      } else {
        await uapi('/api/v1/mappings/unified', {
          method: 'POST',
          body: JSON.stringify({ name: name.trim(), platform, description: description.trim() || undefined, members }),
        });
        onSaved(`${name.trim()} created`);
      }
    } catch (e) {
      const ue = e as UnifiedError;
      if (ue.status === 409 || ue.code === 'CONFLICT') {
        onSaved('Changed by someone else at the same moment: reloaded. Check and try again.');
        return;
      }
      setErr({ text: ue.message, lines: ue.code === 'MEMBER_SPEC_MISMATCH' ? mismatchLines(ue.details) : [] });
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,.75)' }}>
      <div className="flex flex-col rounded border border-[#505050]" style={{ width: 620, maxHeight: '85vh', backgroundColor: '#232225' }}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#404040]">
          <h3 className="text-sm font-semibold text-white">{editing ? `Edit ${editing.name}` : 'New unified symbol'}</h3>
          <button onClick={onClose} className="text-white text-sm px-1" aria-label="Close">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3 text-xs text-white">
          <p className="text-white">
            Groups symbols of one instrument for reporting. Its exposure is the sum of its members.
            It is never mapped to an LP and never hedged; its members are hedged as before.
          </p>

          <div className="flex items-center gap-3">
            <label className="w-24">Platform</label>
            {editing ? (
              <span title="The platform cannot change after creation.">{platformName(editing.platform)}</span>
            ) : (
              <select value={platform} className={iCls} style={{ width: 140 }}
                onChange={(e) => { setPlatform(e.target.value as UPlatform); setMembers([]); setGroupKey(null); if (!nameTouched) setName(''); }}>
                {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            )}
          </div>

          <div>
            <div className="mb-1.5">Members <span className="text-white">(at least 2, from one group: same contract size and currencies)</span></div>
            {candErr ? (
              <div style={{ color: '#d07070' }}>{(candErr as Error).message}</div>
            ) : loadingCand && !candData ? (
              <div>Loading symbols…</div>
            ) : groups.length === 0 ? (
              <div>No symbols on the {platformName(platform)} MASTER can be grouped: a group needs two symbols with the same contract size and currencies, in no other unifier.</div>
            ) : (
              <div className="flex flex-col gap-2">
                {groups.map((g) => {
                  const k = keyOf(g);
                  if (editing && k !== activeKey) return null;
                  return (
                    <div key={k} className="rounded border px-3 py-2"
                      style={{ borderColor: k === activeKey ? '#49b3b3' : '#404040', backgroundColor: '#1a1a1c' }}>
                      <div className="mb-1.5">
                        Contract size <span className="font-mono">{g.contract_size.toLocaleString()}</span>
                        {' · '}<span className="font-mono">{g.currency_base}/{g.currency_profit}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {g.symbols.map((s) => {
                          const on = k === activeKey && members.includes(s);
                          return (
                            <button key={s} onClick={() => toggle(g, s)}
                              className="font-mono rounded px-2 py-0.5"
                              style={on
                                ? { backgroundColor: '#1b2d2d', color: '#fff', border: '1px solid #49b3b3' }
                                : { backgroundColor: '#2a2a2c', color: '#fff', border: '1px solid #505050' }}>
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <label className="w-24">Name</label>
            <input value={name} onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
              placeholder="e.g. XAUUSD.U" className={`${iCls} font-mono`} style={{ width: 200 }} />
            <span className="text-white">Not a platform or an LP symbol.</span>
          </div>
          <div className="flex items-center gap-3">
            <label className="w-24">Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional"
              className={iCls} style={{ flex: 1 }} />
          </div>
        </div>

        {/* The backend's refusal, outside the scroll so it is always in view. */}
        {err && (
          <div className="mx-5 mb-3 rounded px-3 py-2 text-xs" style={{ backgroundColor: '#2a1a1d', border: '1px solid #8a4a52', color: '#fff' }}>
            <div>{err.text}</div>
            {err.lines.length > 0 && (
              <div className="mt-1 font-mono">{err.lines.map((l) => <div key={l}>{l}</div>)}</div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between px-5 py-3 border-t border-[#404040]">
          <span className="text-xs text-white font-mono">{members.length ? members.join(', ') : ''}</span>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded" style={btn('plain')}>Cancel</button>
            <button onClick={save} disabled={!canSave} className="text-xs px-3 py-1.5 rounded" style={btn('ok', !canSave)}>
              {busy ? 'Saving…' : editing ? 'Save' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteUnifier({ u, onClose, onDeleted }: { u: Unifier; onClose: () => void; onDeleted: (msg: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      await uapi(`/api/v1/mappings/unified/${u.id}`, { method: 'DELETE' });
      onDeleted(`${u.name} deleted`);
    } catch (e) {
      const ue = e as UnifiedError;
      if (ue.status === 409 || ue.code === 'CONFLICT') { onDeleted('Changed by someone else at the same moment: reloaded.'); return; }
      setErr(ue.message); setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,.75)' }}>
      <div className="rounded border border-[#505050]" style={{ width: 360, backgroundColor: '#232225' }}>
        <div className="px-4 py-3 border-b border-[#404040] text-sm font-semibold text-white">Delete unified symbol</div>
        <div className="px-4 py-3 text-xs text-white">
          Delete <span className="font-mono">{u.name}</span>? Its members (<span className="font-mono">{u.members.join(', ')}</span>) become stand-alone symbols again. Their mappings and hedging do not change.
          {err && <div className="mt-2" style={{ color: '#d07070' }}>{err}</div>}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-[#404040]">
          <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 rounded" style={btn('plain')}>Cancel</button>
          <button onClick={run} disabled={busy} className="text-xs px-3 py-1.5 rounded" style={btn('danger', busy)}>{busy ? 'Deleting…' : 'Delete'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Section ──────────────────────────────────────────────────
export function UnifiedSymbolsSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const [platform, setPlatform] = useState<'' | UPlatform>('');
  const [dialog, setDialog]     = useState<{ editing: Unifier | null } | null>(null);
  const [deleting, setDeleting] = useState<Unifier | null>(null);
  const [msg, setMsg]           = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['unified-list', platform],
    queryFn:  () => uapi<{ total: number; unifiers: Unifier[] }>(
      `/api/v1/mappings/unified${platform ? `?platform=${platform}` : ''}`),
    staleTime: 10_000,
  });
  const list = data?.unifiers ?? [];

  const done = (text: string) => {
    setDialog(null); setDeleting(null); setMsg(text);
    qc.invalidateQueries({ queryKey: ['unified-list'] });
    qc.invalidateQueries({ queryKey: ['unified-candidates'] });
  };

  const thS: React.CSSProperties = { padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#fff', fontSize: 12, borderBottom: '1px solid #3a3a3c', backgroundColor: '#1e1e20', position: 'sticky', top: 0 };
  const tdS: React.CSSProperties = { padding: '6px 10px', color: '#fff', fontSize: 12, borderBottom: '1px solid #2e2e30', verticalAlign: 'top' };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-2 border-b border-[#404040] flex items-center gap-3 flex-shrink-0" style={{ backgroundColor: '#252427' }}>
        <span className="text-[10px] text-white uppercase tracking-wider font-medium">Unified symbols</span>
        <select value={platform} onChange={(e) => setPlatform(e.target.value as '' | UPlatform)} className={iCls} style={{ width: 140 }}>
          <option value="">All platforms</option>
          {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <span className="text-[10px] text-[#49b3b3] font-mono">{list.length}</span>
        {msg && <span className="text-xs text-white">{msg}</span>}
        <div className="flex-1" />
        <button onClick={() => refetch()} className="text-[10px] text-white">Refresh</button>
        {canEdit && (
          <button onClick={() => { setMsg(null); setDialog({ editing: null }); }} className="text-xs px-3 py-1 rounded" style={btn('ok')}>
            New unified symbol
          </button>
        )}
      </div>

      <div className="px-4 py-2 text-xs text-white flex-shrink-0" style={{ backgroundColor: '#2a292c' }}>
        A unified symbol groups symbols of one instrument for reporting (Net Exposure, Cockpit). It is never mapped to an LP or hedged; its members keep their own mappings and hedging rules.
      </div>

      <div className="flex-1 overflow-auto" style={{ backgroundColor: '#232326' }}>
        {error ? (
          <div className="py-10 text-center text-xs" style={{ color: '#d07070' }}>{(error as Error).message}</div>
        ) : isLoading ? (
          <div className="py-10 text-center text-xs text-white">Loading…</div>
        ) : list.length === 0 ? (
          <div className="py-10 text-center text-xs text-white">No unified symbols.</div>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {['Name', 'Platform', 'Members', 'Contract size', 'Currencies', 'Status', 'Description', ''].map((h) => <th key={h} style={thS}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id}>
                  <td style={{ ...tdS, fontWeight: 600 }} className="font-mono">{u.name}</td>
                  <td style={tdS}>{platformName(u.platform)}</td>
                  <td style={tdS} className="font-mono">{u.members.join(', ')}</td>
                  <td style={tdS} className="font-mono">{u.contract_size != null ? u.contract_size.toLocaleString() : '—'}</td>
                  <td style={tdS} className="font-mono">{u.currency_base ?? '—'}/{u.currency_profit ?? '—'}</td>
                  <td style={tdS}><StatusBadge u={u} /></td>
                  <td style={tdS}>{u.description || ''}</td>
                  <td style={{ ...tdS, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {canEdit && (
                      <span className="inline-flex gap-1.5">
                        <button onClick={() => { setMsg(null); setDialog({ editing: u }); }} className="text-xs px-2 py-0.5 rounded" style={btn('plain')}>Edit</button>
                        <button onClick={() => { setMsg(null); setDeleting(u); }} className="text-xs px-2 py-0.5 rounded" style={btn('danger')}>Delete</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {dialog && <UnifierDialog editing={dialog.editing} onClose={() => setDialog(null)} onSaved={done} />}
      {deleting && <DeleteUnifier u={deleting} onClose={() => setDeleting(null)} onDeleted={done} />}
    </div>
  );
}
